import { isEmpty, uniqBy } from 'lodash';
import { helpers } from '@/scripts/helpers';
import moment from 'moment';

export const OPPORTUNITY_EDIT_CONTEXT = {
    Details: 'Details',
    All: 'All',
};

export const OpportunityHelpers = {
    getStatus(opportunityResponse) {
        const dealStatus = opportunityResponse.DealStatus;
        if(dealStatus) {
            return {
                id: dealStatus.Id,
                name: dealStatus.Name,
                isWon: dealStatus.IsWon,
                backgroundClass: helpers.getStatusBackgroundClass(
                    dealStatus.Color
                )
            };
        }

        return {}
    },

    getSimpleEntry(opportunityResponse) {
        const simpleEntry = {};

        if (opportunityResponse.PriceType) {
            simpleEntry.priceType = {
                id: opportunityResponse.PriceType.Id,
                name: opportunityResponse.PriceType.Name
            };
        }

        return simpleEntry;
    },

    getType(opportunityResponse) {
        return {
            id: opportunityResponse.DealType ? opportunityResponse.DealType.Id : null,
            name: opportunityResponse.DealType ? opportunityResponse.DealType.Name : null,
        };
    },

    getDaysOpen(opportunityResponse) {
        let daysOpen = '';

        const openDate = moment.utc(opportunityResponse.CreatedAt).local();

        if (opportunityResponse.IsWon !== null && opportunityResponse.CloseDate) {
            const closeDate = moment.utc(opportunityResponse.CloseDate).local();
            daysOpen = closeDate.diff(openDate, 'days');
        } else {
            const currentDate = moment().local();
            daysOpen = currentDate.diff(openDate, 'days');
        }

        return daysOpen;
    },

    getContact(opportunityResponse) {
        return {
            id: opportunityResponse.Contact.Id,
            name: opportunityResponse.Contact
                ? helpers.getName(
                    opportunityResponse.Contact.FirstName,
                    opportunityResponse.Contact.LastName
                )
                : null,
            email: opportunityResponse.Contact.Email,
            imageUrl: opportunityResponse.Contact
                ? opportunityResponse.Contact.ProfileImageUrl || require('@/assets/images/avatar.png')
                : require('@/assets/images/avatar.png'),
            clientId: opportunityResponse.Contact
                ? opportunityResponse.Contact.ClientId
                : null,
            companyId: opportunityResponse.Contact.Company ? opportunityResponse.Contact.Company.Id : null
        };
    },

    getSecondaryContacts(opportunityResponse) {
        return opportunityResponse.DealContacts.map(
            ({ ContactId, Contact }) => ({
                id: ContactId,
                name: helpers.getName(Contact.FirstName, Contact.LastName),
                email: Contact.Email,
                imageUrl: Contact.ProfileImageUrl,
                clientId: Contact.ClientId,
                companyId: Contact.CompanyId,
                title: Contact.Title
            })
        );
    },

    getCompany(opportunityResponse) {
        const company = opportunityResponse.Contact.Company;
        if (company) {
            return {
                id: company.Id,
                name: company.Name,
                imageUrl:
                    company.LogoUrl ||
                    require('@/assets/images/company-avatar.png')
            };
        }

        return null;
    },

    getSalesperson(opportunityResponse) {
        if (opportunityResponse.CreatedByUser) {
            return {
                id: opportunityResponse.CreatedByUser.Id,
                name: helpers.getName(
                    opportunityResponse.CreatedByUser.FirstName,
                    opportunityResponse.CreatedByUser.LastName
                ),
                email: opportunityResponse.CreatedByUser.Email,
                imageUrl:
                    opportunityResponse.CreatedByUser.ProfileImageUrl ||
                    require('@/assets/images/avatar.png')
            };
        }
    },

    getSalespersons(opportunityResponse) {
        return uniqBy(opportunityResponse.DealSalesPersons
            .filter(({ SalesPerson }) => SalesPerson != null)
            .map(({ SalesPersonId, SalesPerson }) => ({
                    id: SalesPersonId,
                    name: SalesPerson
                        ? helpers.getName(SalesPerson.FirstName, SalesPerson.LastName)
                        : null,
                    email: SalesPerson ? SalesPerson.Email : null,
                    imageUrl: SalesPerson ? SalesPerson.ProfileImageUrl : null
                })
            ), 'id');
    },

    getRegisteredDeals(opportunityResponse) {
        if (!isEmpty(opportunityResponse.RegisteredDeals)) {
            return opportunityResponse.RegisteredDeals.map((deal) => ({
                id: deal.Id,
                name: deal.Name,
                isApproved: deal.IsApproved,
                value: deal.Value,
                description: deal.Description,
                registrationNumber:
                deal.RegistrationNumber,
                reason: deal.Reason,
                dealId: deal.DealId,
                agency: {
                    id: deal.Agency.Id,
                    name: deal.Agency.Name
                }
            }));
        }

        return [];
    },

    getLineItems(opportunityResponse, discountUnit) {
        const billingCycles = helpers.getSolutionBillingCycles();
        return opportunityResponse.LineItems.map((lineItem) => {
            const billingCycle = billingCycles.find(
                (cycle) =>
                    cycle.value === lineItem.BillingCycle
            );

            const term = this.getLineItemTerm(
                lineItem.TermInMonths,
                billingCycle.name
            );

            const noDiscountSubtotal =
                lineItem.UnitPrice *
                lineItem.Quantity *
                term;

            const subtotal = lineItem.UnitDiscount
                ? noDiscountSubtotal - this.getLineItemDiscountAmount(lineItem, discountUnit, noDiscountSubtotal, lineItem.Quantity)
                : noDiscountSubtotal;

            return {
                id: lineItem.Id,
                solutionId: lineItem.Solution.Id,
                connectWiseDealLineItemId: lineItem.ConnectWiseDealLineItemId,
                name: lineItem.Solution.Name,
                description: lineItem.Solution.Description,
                previewImageUrl: lineItem.Solution.Url,
                imgUrl: lineItem.Solution.Url,
                videoUrl: lineItem.Solution.VideoUrl,
                price: lineItem.UnitPrice,
                cost: lineItem.UnitCost,
                margin:
                    lineItem.UnitPrice -
                    lineItem.UnitCost,
                qty: lineItem.Quantity,
                billingCycle: billingCycle.name,
                termInMonths: lineItem.TermInMonths,
                term,
                discount: lineItem.UnitDiscount,
                discountUnit,
                bundles: lineItem.Solution.Bundles.map(
                    ({ Id, Name, ImageUrl }) => ({
                        id: Id,
                        name: Name,
                        imageUrl: ImageUrl
                    })
                ),
                subtotal
            };
        });
    },

    getLineItemTerm(termInMonths, billingCycle) {
        const terms = {
            'One-time': 1,
            Monthly: termInMonths,
            Quarterly: termInMonths / 4,
            'Semi-annually': termInMonths / 6,
            Annually: termInMonths / 12
        };
        return terms[billingCycle] || 1;
    },

    getLineItemDiscountAmount(lineItem, discountUnit, total, qty) {
        if (discountUnit === '%') {
            return (
                (total * lineItem.UnitDiscount) / 100
            );
        } else {
            return lineItem.UnitDiscount * qty;
        }
    },

    getDiscountUnit(opportunityResponse) {
        return (
            opportunityResponse.DiscountTypeId === 1
                ? '%'
                : '$'
        );
    },

    getDiscountPercentage(discount, discountUnit, preDiscountPrice) {
        if (discount) {
            if (discountUnit === '%') {
                return discount;
            } else {
                return (
                    (discount * 100) /
                    preDiscountPrice
                ).toFixed(2);
            }
        }
        return 0;
    },

    getItemizedEntryTotalPrice(opportunity, includeDiscount = true) {
        let totalPrice = 0;

        const {
            hasDiscount,
            discountType, // i.e. "total" || "item"
            totalDiscountValue, // This is the discount value (i.e. 10)
            totalDiscountType, // i.e. "percentage" || "amount"
            itemDiscountType, //  i.e.  "percentage" || "amount"
            solutions // Hybrid between the Solutions table and the Deal Line Items table
        } = opportunity.itemizedEntry;

        const isTotalDiscount = discountType === 'total';
        const isPercentageDiscount = totalDiscountType === 'percentage' && itemDiscountType === 'percentage'; // Both of these values were set at the same time
        const discount = isTotalDiscount ? totalDiscountValue : 0;

        solutions?.forEach((solution) => {
            const {
                price,
                qty,
                termInMonths,
                billingCycle,
                discount
            } = solution;

            const billingCycleValue = this.getLineItemTerm(termInMonths, billingCycle);
            const basePrice = price * qty * billingCycleValue;

            if (includeDiscount && hasDiscount) {
                const discountAmount = isTotalDiscount ? totalDiscountValue : (discount || 0);

                if (isTotalDiscount) {
                    if (isPercentageDiscount) {
                        totalPrice += basePrice - (basePrice * (discountAmount / 100));
                    } else {
                        totalPrice += basePrice;
                    }
                } else {
                    if (isPercentageDiscount) {
                        totalPrice += basePrice - (basePrice * (discountAmount / 100));
                    } else {
                        totalPrice += basePrice - discountAmount * qty * billingCycleValue;
                    }
                }
            } else {
                totalPrice += basePrice;
            }
        });

        if (includeDiscount && hasDiscount && isTotalDiscount && !isPercentageDiscount) {
            totalPrice -= discount;
        }

        return totalPrice;
    },

    getItemizedEntryTotalCost(opportunity) {
        return opportunity.itemizedEntry.solutions.reduce(
            (acc, { cost, term, qty }) => acc + cost * qty * term,
            0
        )
    },

    getSimpleEntryTotalCost(opportunity) {
        const { priceType, duration, cost } = opportunity.simpleEntry;
        return priceType?.name?.toLowerCase().includes('fixed') ? cost : cost * duration;
    },

    getSimpleEntryTotalPrice(opportunity, calculateWithDiscount = true) {
        const { priceType, duration, price, discount, discountType } = opportunity.simpleEntry;
        const isFixedBid = priceType?.name?.toLowerCase().includes('fixed');

        const basePrice = isFixedBid ? price : price * duration;

        let totalDiscount = 0;
        if (calculateWithDiscount) {
            if (discountType === 'percentage') {
                totalDiscount = (basePrice * discount) / 100;
            } else {
                totalDiscount = discount;
            }
        }

        return basePrice - totalDiscount;
    },

    getOpportunityTotal(opportunity) {
        if(opportunity.pricingDetails === 'simple') {
            return OpportunityHelpers.getSimpleEntryTotalPrice(opportunity);
        }

        return OpportunityHelpers.getItemizedEntryTotalPrice(opportunity);
    },

    getUpfrontPayment(solutions) {
        return solutions
            .filter(({ billingCycle }) => ['one-time', 'hourly'].includes(billingCycle.toLowerCase()))
            .reduce((acc, { price, qty, discount, discountUnit }) => {
                let discountAmount = 0;

                if (discount > 0) {
                    discountAmount = discountUnit === '%' ? (price * discount) / 100 : discount * qty;
                }

                return acc + (price * qty - discountAmount);
            }, 0);
    },

    getAnnualPayment(solutions) {
        return solutions
            .filter(({ billingCycle }) => ['monthly', 'quarterly', 'semi-annually', 'annually'].includes(billingCycle.toLowerCase()))
            .reduce((acc, { price, qty, term, discount, discountUnit }) => {
                let discountAmount = 0;
                
                if (discount > 0) {
                    discountAmount = discountUnit === '%' ? (price * discount) / 100 : discount * qty * term;
                }

                return acc + (price * qty * term) - discountAmount;
            }, 0);
    },

    getTermSuffix(term) {
        switch (term) {
            case 'One-time':
                return '';
            case 'Hourly':
                return 'hrs';
            case 'Monthly':
                return 'mos';
            case 'Quarterly':
                return 'qrs';
            case 'Semi-annually':
                return 'sa';
            case 'Annually':
                return 'yrs';
        }
    },

    getPriceSuffix(price) {
        switch (price) {
            case 'One-time':
                return '';
            case 'Hourly':
                return '/hr';
            case 'Monthly':
                return '/mo';
            case 'Quarterly':
                return '/qr';
            case 'Semi-annually':
                return '/sa';
            case 'Annually':
                return '/yr';
        }
    },
};
